import { useEffect, useMemo } from "react"

import { useLazyGetPhysicalStatusesWidgetQuery } from "store/api/adminApi"
import { useGetEnterpriseDashboardThermostatWidgetsQuery } from "store/api/widgetsApi"
import { ApiTypes } from "types"

export const initialPhysicalStatusWidgetData = {
  installed: 0,
  inInventory: 0,
  inStock: 0,
  missing: 0,
  damaged: 0,
  transferring: 0,
  production: 0,
  warranty: 0,
  packed: 0,
  preinstalled: 0,
}

export type PhysicalStatusWidgetData = typeof initialPhysicalStatusWidgetData

const mapAdminWidgetData = (
  data?: Partial<{
    installed: number
    inventory: number
    stocked: number
    missing: number
    damaged: number
    transferring: number
    production: number
    warranty: number
    packed: number
    preinstalled: number
  }>,
): PhysicalStatusWidgetData => ({
  installed: data?.installed ?? 0,
  inInventory: data?.inventory ?? 0,
  inStock: data?.stocked ?? 0,
  missing: data?.missing ?? 0,
  damaged: data?.damaged ?? 0,
  transferring: data?.transferring ?? 0,
  production: data?.production ?? 0,
  warranty: data?.warranty ?? 0,
  packed: data?.packed ?? 0,
  preinstalled: data?.preinstalled ?? 0,
})

const mapEnterpriseWidgetData = (
  data?: ApiTypes.Res.EnterpriseDashboardDeviceWidgets["thermostat_statuses"],
): PhysicalStatusWidgetData => ({
  ...initialPhysicalStatusWidgetData,
  installed: data?.installed ?? 0,
  inInventory: data?.inventory ?? 0,
  inStock: data?.other ?? 0,
  missing: data?.missing ?? 0,
  transferring: data?.transferring ?? 0,
  warranty: data?.warranty ?? 0,
  preinstalled: data?.preinstalled ?? 0,
})

export const useWidgetPhysicalStatusData = (variant: "default" | "enterprise") => {
  const isEnterprise = variant === "enterprise"
  const [triggerGetPhysicalStatusesWidget, { isFetching: isAdminFetching, data: adminData }] =
    useLazyGetPhysicalStatusesWidgetQuery()
  const { data: enterpriseData, isFetching: isEnterpriseFetching } = useGetEnterpriseDashboardThermostatWidgetsQuery(
    undefined,
    { skip: !isEnterprise },
  )

  useEffect(() => {
    if (isEnterprise) return

    const fetchData = async () => {
      try {
        await triggerGetPhysicalStatusesWidget()
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [isEnterprise, triggerGetPhysicalStatusesWidget])

  const widgetData = useMemo(
    () =>
      isEnterprise
        ? mapEnterpriseWidgetData(enterpriseData?.data?.thermostat_statuses)
        : mapAdminWidgetData(adminData?.data),
    [adminData?.data, enterpriseData?.data?.thermostat_statuses, isEnterprise],
  )
  const total = isEnterprise ? enterpriseData?.data?.thermostat_statuses?.total : undefined

  return {
    isEnterprise,
    isFetching: isEnterprise ? isEnterpriseFetching : isAdminFetching,
    loaded: isEnterprise ? !!enterpriseData?.data?.thermostat_statuses : !!adminData?.data,
    total,
    widgetData,
  }
}
